import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  TextField,
  Alert,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  SPLITTER_PADROES_CORES,
  PadraoCoresSplitter,
  getPadraoCoresSplitterLabel,
} from '../../utils/fiberColors';

export interface SplitterEditConfig {
  padrao_cores: PadraoCoresSplitter;
  saida1_porcentagem?: number;
  saida2_porcentagem?: number;
}

interface SplitterEditPadraoModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: SplitterEditConfig) => void;
  padraoAtual?: PadraoCoresSplitter;
  splitterConfig?: string;
  is1x2?: boolean;
  saida1Atual?: number;
  saida2Atual?: number;
  isFullscreen?: boolean;
  container?: HTMLElement | null;
}

const SplitterEditPadraoModal: React.FC<SplitterEditPadraoModalProps> = ({
  open,
  onClose,
  onConfirm,
  padraoAtual = 'sem_padrao',
  splitterConfig = '1x2',
  is1x2 = false,
  saida1Atual = 50,
  saida2Atual = 50,
  isFullscreen = false,
  container,
}) => {
  const [padraoCores, setPadraoCores] = useState<PadraoCoresSplitter>(padraoAtual);
  const [saida1Pct, setSaida1Pct] = useState(saida1Atual);
  const [saida2Pct, setSaida2Pct] = useState(saida2Atual);
  const [erroPotencia, setErroPotencia] = useState('');

  const menuZIndex = isFullscreen ? 10006 : 1600;
  const selectMenuProps = {
    MenuProps: { disablePortal: true, sx: { zIndex: menuZIndex } },
  };
  const selectFormControlSx = { '& .MuiPopover-root': { zIndex: menuZIndex } };

  useEffect(() => {
    if (open) {
      setPadraoCores(padraoAtual);
      setSaida1Pct(saida1Atual);
      setSaida2Pct(saida2Atual);
      setErroPotencia('');
    }
  }, [open, padraoAtual, saida1Atual, saida2Atual]);

  const handleSaida1Change = (value: number) => {
    if (value >= 1 && value <= 99) {
      setSaida1Pct(value);
      setSaida2Pct(100 - value);
      setErroPotencia('');
    }
  };

  const handleSaida2Change = (value: number) => {
    if (value >= 1 && value <= 99) {
      setSaida2Pct(value);
      setSaida1Pct(100 - value);
      setErroPotencia('');
    }
  };

  const handleConfirm = () => {
    if (is1x2) {
      if (saida1Pct + saida2Pct !== 100 || saida1Pct < 1 || saida2Pct < 1) {
        setErroPotencia('A soma das potências das saídas deve ser 100% (mínimo 1% cada).');
        return;
      }
      onConfirm({
        padrao_cores: padraoCores,
        saida1_porcentagem: saida1Pct,
        saida2_porcentagem: saida2Pct,
      });
      return;
    }
    onConfirm({ padrao_cores: padraoCores });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      container={isFullscreen && container ? container : undefined}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
      sx={{ zIndex: isFullscreen ? 10005 : 1500 }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h6" component="span" sx={{ fontSize: '1rem' }}>
            Editar Splitter {splitterConfig}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Padrão atual: <strong>{getPadraoCoresSplitterLabel(padraoAtual)}</strong>
          </Typography>

          <FormControl fullWidth size="small" required sx={selectFormControlSx}>
            <InputLabel>Padrão de cores</InputLabel>
            <Select
              label="Padrão de cores"
              value={padraoCores}
              onChange={(e) => setPadraoCores(e.target.value as PadraoCoresSplitter)}
              {...selectMenuProps}
            >
              {SPLITTER_PADROES_CORES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Sem Padrão, ABNT ou EIA-598-A</FormHelperText>
          </FormControl>

          {is1x2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Potência de saída (% da potência de entrada)
              </Typography>
              <TextField
                label="Saída 1 (%)"
                type="number"
                size="small"
                value={saida1Pct}
                onChange={(e) => handleSaida1Change(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 1, max: 99 }}
                fullWidth
              />
              <TextField
                label="Saída 2 (%)"
                type="number"
                size="small"
                value={saida2Pct}
                onChange={(e) => handleSaida2Change(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 1, max: 99 }}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary">
                Total: {saida1Pct + saida2Pct}%
              </Typography>
              {erroPotencia && <Alert severity="error" sx={{ py: 0 }}>{erroPotencia}</Alert>}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleConfirm}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitterEditPadraoModal;
